import { forwardRef } from 'react'
import { cn } from '../cn'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  /** The value shown was clamped before use (e.g. a discount bounded to
   * 0–100 %) — an amber border, weaker than `invalid`'s red, since the
   * field still submits fine. Ignored when `invalid` is also set. */
  warning?: boolean
  /** Right-aligns the value with tabular figures. Use for every amount field. */
  numeric?: boolean
}

const base =
  'w-full rounded-[var(--radius-control)] border bg-surface px-2.5 text-base text-ink ' +
  'placeholder:text-ink-faint transition-colors duration-[var(--duration-fast)] ' +
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted'

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, warning, numeric, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        base,
        'h-8',
        numeric && 'text-right tabular',
        invalid ? 'border-danger' : warning ? 'border-warning' : 'border-rule-strong hover:border-ink-faint',
        className,
      )}
      {...props}
    />
  )
})

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ className, invalid, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          base,
          'min-h-20 py-1.5 leading-(--leading-normal) resize-y',
          invalid ? 'border-danger' : 'border-rule-strong hover:border-ink-faint',
          className,
        )}
        {...props}
      />
    )
  },
)
